import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Pagination,
  CircularProgress,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import TokenDetailsModal from "./TokenDetailsModal"; // Adjust path if needed
import axiosInstance from "../../../../api/axiosConfig";

function CustomerTransactionsModal({ open, onClose, customer }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination states
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;

  // Selected Token ID for TokenDetailsModal
  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [openTokenModal, setOpenTokenModal] = useState(false);

  // Fetch customer transactions
  const fetchTransactions = useCallback(
    async (currentPage) => {
      if (!customer?.email) return;

      try {
        setLoading(true);
        const response = await axiosInstance.get(
          "/marketplace/getequity/get-customer-transactions",
          {
            params: {
              email: customer.email,
              page: currentPage,
              limit: limit,
            },
          },
        );

        if (response.data?.status === "successful") {
          setTransactions(response.data.data || []);
          setTotalPages(response.data.totalPages || 1);
        }
      } catch (error) {
        console.error("Error fetching transactions: ", error);
        toast.error(
          error.response?.data?.message || "Failed to load transactions.",
        );
      } finally {
        setLoading(false);
      }
    },
    [customer?.email, limit],
  );

  useEffect(() => {
    if (open && customer?.email) {
      setPage(1);
      setSearchTerm("");
      fetchTransactions(1);
    }
  }, [open, customer, fetchTransactions]);

  // Handle card click to view Token Details
  const handleCardClick = (tokenId) => {
    if (!tokenId) return;
    setSelectedTokenId(tokenId);
    setOpenTokenModal(true);
  };

  // Helper calculation for Days Remaining until Maturity
  const calculateDaysRemaining = (maturityDate) => {
    if (!maturityDate) return 0;
    const now = new Date();
    const maturity = new Date(maturityDate);
    const diffTime = maturity - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : 0;
  };

  // Client-side filtering across core visible fields
  const filteredTransactions = transactions.filter((txn) => {
    const searchLower = searchTerm.toLowerCase();
    const tokenName = txn.token?.name?.toLowerCase() || "";
    const type = txn.type?.toLowerCase() || "";
    const status = txn.status?.toLowerCase() || "";
    const category = txn.token?.investment_category?.toLowerCase() || "";
    const reference = txn.reference?.toLowerCase() || "";

    return (
      tokenName.includes(searchLower) ||
      type.includes(searchLower) ||
      status.includes(searchLower) ||
      category.includes(searchLower) ||
      reference.includes(searchLower)
    );
  });

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: "2px solid #000000",
            bgcolor: "#ffffff",
            color: "#000000",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid #000000",
            pb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, textTransform: "uppercase" }}
            >
              Customer Transactions
            </Typography>
            {customer && (
              <Typography variant="caption" sx={{ color: "#666666" }}>
                {customer.name} ({customer.email})
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} sx={{ color: "#000000" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Search Bar */}
          <Box
            sx={{ mb: 3, mt: 1, display: "flex", justifyContent: "flex-end" }}
          >
            <TextField
              size="small"
              placeholder="Search asset name, type, category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#000000" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 350 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  "& fieldset": { borderColor: "#000000", borderWidth: "1px" },
                  "&:hover fieldset": { borderColor: "#000000" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#000000",
                    borderWidth: "2px",
                  },
                },
              }}
            />
          </Box>

          {/* Cards Grid */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: "#000000" }} />
            </Box>
          ) : filteredTransactions.length === 0 ? (
            <Box
              sx={{ textAlign: "center", py: 6, border: "1px dashed #000000" }}
            >
              <Typography variant="body2" color="text.secondary">
                No transactions found for this customer.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredTransactions.map((txn) => {
                const price = txn.price || txn.token?.price?.buy || 0;
                const volume = txn.volume || 0;
                const amount = volume * price;
                const currency = txn.currency || txn.token?.currency || "NGN";
                const daysLeft = calculateDaysRemaining(txn.token?.maturity);

                return (
                  <Grid item xs={12} sm={6} key={txn._id}>
                    <Card
                      elevation={0}
                      onClick={() => handleCardClick(txn.token?._id)}
                      sx={{
                        border: "1px solid #000000",
                        borderRadius: 0,
                        cursor: txn.token?._id ? "pointer" : "default",
                        transition: "all 0.15s ease",
                        "&:hover": {
                          boxShadow: "4px 4px 0px #000000",
                          transform: "translate(-2px, -2px)",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                        {/* Header: Title & Badges */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            mb: 1,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 800, pr: 1, color: "#000000" }}
                          >
                            {txn.token?.name || "N/A"}
                          </Typography>
                          <Chip
                            label={txn.status || "Unknown"}
                            size="small"
                            sx={{
                              borderRadius: 0,
                              fontWeight: 700,
                              fontSize: "0.65rem",
                              bgcolor:
                                txn.status?.toLowerCase() === "completed"
                                  ? "#000000"
                                  : "#e0e0e0",
                              color:
                                txn.status?.toLowerCase() === "completed"
                                  ? "#ffffff"
                                  : "#000000",
                            }}
                          />
                        </Box>

                        {/* Secondary Header: Type & Category */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            mb: 1.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Chip
                            label={txn.type || "N/A"}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderRadius: 0,
                              borderColor: "#000000",
                              fontSize: "0.7rem",
                              fontWeight: 700,
                            }}
                          />
                          <Chip
                            label={txn.token?.investment_category || "N/A"}
                            size="small"
                            sx={{
                              borderRadius: 0,
                              bgcolor: "#f0f0f0",
                              color: "#000000",
                              fontSize: "0.7rem",
                            }}
                          />
                        </Box>

                        <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                        {/* Details Grid */}
                        <Grid
                          container
                          spacing={1}
                          sx={{ fontSize: "0.8rem", color: "#333333" }}
                        >
                          <Grid item xs={6}>
                            <strong>Interest:</strong>{" "}
                            {txn.token?.interest
                              ? `${txn.token.interest}%`
                              : "0%"}
                          </Grid>
                          <Grid item xs={6}>
                            <strong>Tenor:</strong>{" "}
                            {txn.token?.tenor
                              ? `${txn.token.tenor} days`
                              : "N/A"}
                          </Grid>
                          <Grid item xs={6}>
                            <strong>Days Left:</strong> {daysLeft} d
                          </Grid>
                          <Grid item xs={6}>
                            <strong>Maturity:</strong>{" "}
                            {txn.token?.maturity
                              ? new Date(txn.token.maturity).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                  },
                                )
                              : "N/A"}
                          </Grid>
                          <Grid item xs={6}>
                            <strong>Units:</strong> {volume.toLocaleString()}
                          </Grid>
                          <Grid item xs={6}>
                            <strong>Price:</strong> {currency}{" "}
                            {price.toLocaleString()}
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                        {/* Footer: Date & Total Amount */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 1,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#666666" }}
                          >
                            {txn.createdAt
                              ? new Date(txn.createdAt).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  },
                                )
                              : "N/A"}
                          </Typography>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800, color: "#000000" }}
                          >
                            Value: {currency} {amount.toLocaleString()}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}

          {/* Pagination Controls */}
          {!loading && totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => {
                  setPage(value);
                  fetchTransactions(value);
                }}
                shape="rounded"
                sx={{
                  "& .MuiPaginationItem-root": {
                    borderRadius: 0,
                    color: "#000000",
                    "&.Mui-selected": {
                      bgcolor: "#000000",
                      color: "#ffffff",
                      "&:hover": { bgcolor: "#333333" },
                    },
                  },
                }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Box
            onClick={onClose}
            sx={{
              px: 3,
              py: 1,
              bgcolor: "#000000",
              color: "#ffffff",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: "0.875rem",
              "&:hover": { bgcolor: "#333333" },
            }}
          >
            Close
          </Box>
        </DialogActions>
      </Dialog>

      {/* Embedded Token Details Modal */}
      {openTokenModal && (
        <TokenDetailsModal
          open={openTokenModal}
          onClose={() => setOpenTokenModal(false)}
          tokenId={selectedTokenId}
        />
      )}
    </>
  );
}

export default CustomerTransactionsModal;
